/**
 * The bar a call collapses into, docked above whatever screen is showing.
 *
 * This is the piece that makes an overlay behave like a call rather than a modal:
 * the connection is untouched, the timer keeps running, and going back into it
 * restores the same call at the same elapsed time.
 */
import { formatDuration } from '../lib/format.ts';
import { useCall } from './CallContext.tsx';
import { PhoneIcon } from './CallIcons.tsx';
import './CallOverlay.css';

export function CallPill(): React.ReactElement | null {
  const { call, minimized, setMinimized, hangUp } = useCall();

  // Only a call that is actually running collapses. One that is ringing, failed
  // or finished has nothing to go back to and would leave a bar that outlives it.
  if (!call || !minimized || call.phase !== 'active') return null;

  return (
    <div
      className="call-pill"
      role="button"
      tabIndex={0}
      onClick={() => setMinimized(false)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') setMinimized(false);
      }}
    >
      <span className="call-pill-dot" />
      <span className="call-pill-name">{call.peerName}</span>
      <span className="call-pill-timer">{formatDuration(call.elapsedSeconds * 1000)}</span>
      <button
        type="button"
        className="call-pill-end"
        aria-label="Завершить звонок"
        onClick={(event) => {
          // Without this the tap would also restore the full-screen call, on its
          // way out.
          event.stopPropagation();
          hangUp();
        }}
      >
        <PhoneIcon size={14} />
      </button>
    </div>
  );
}
